'use client';

import { isValidElement, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, Info, Loader2, XCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  Toast,
  ToastAction,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from '@/components/ui/toast';

/**
 * Leading icon per variant. Decorative — aria-hidden. `loading` spins.
 * Returns null for default/destructive (no leading glyph, preserves legacy look).
 */
function VariantIcon({ variant }: { variant?: string | null }) {
  const base = 'h-5 w-5 shrink-0';
  switch (variant) {
    case 'success':
      return <CheckCircle2 aria-hidden="true" className={`${base} text-emerald-500`} />;
    case 'error':
      return <XCircle aria-hidden="true" className={`${base} text-red-500`} />;
    case 'info':
      return <Info aria-hidden="true" className={`${base} text-brand-500`} />;
    case 'warning':
      return <AlertTriangle aria-hidden="true" className={`${base} text-amber-500`} />;
    case 'loading':
      return (
        <Loader2
          aria-hidden="true"
          className={`${base} animate-spin text-gray-500 dark:text-gray-300`}
        />
      );
    default:
      return null;
  }
}

/**
 * Toaster - Toast notification container
 *
 * 🔧 FIX: Renders via React Portal to document.body to escape the
 * screen-filter-wrapper's `filter` property, which breaks `position: fixed`.
 * Without the portal, toasts appear at the top of the PAGE instead of
 * the top of the VIEWPORT (not visible when scrolled down).
 *
 * See: globals.css "SCREEN CONTRAST FILTER" section
 */
export function Toaster() {
  const { toasts } = useToast();
  const [mounted, setMounted] = useState(false);

  // Portal mount detection for client-side rendering
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time SSR→client mount flag; runs once, no cascade possible
    setMounted(true);
  }, []);

  const toasterContent = (
    <ToastProvider>
      {toasts.map(function ({ id, title, description, action, icon, variant, onClick, ...props }) {
        // action may be a pre-built ReactElement (legacy) or a {label,onClick}
        // descriptor (sonner-style). Build a ToastAction from the descriptor.
        let actionNode: React.ReactNode = null;
        if (action) {
          if (isValidElement(action)) {
            actionNode = action;
          } else if (typeof action === 'object' && 'label' in action && 'onClick' in action) {
            const { label, onClick: actionOnClick } = action as {
              label: string;
              onClick: () => void;
            };
            actionNode = (
              <ToastAction
                altText={label}
                // High-contrast pill: solid theme-foreground fill + theme-background text
                // guarantees AA contrast on EVERY variant tint (info/success/warning/
                // destructive). The base primitive's `bg-transparent` + inherited variant
                // text color made the action button the least-visible element in the toast
                // (Glass Morphism Interaction Failure). bg/text are overridden AS A PAIR
                // (see ui-components.md "Tooltip + glass-popover → invisible text"); cn()
                // merges these AFTER the base so they win. Drives the Undo flow (useUndoAction).
                className="border-transparent bg-foreground font-semibold text-background shadow-sm hover:bg-foreground/90 hover:text-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                onClick={(e) => {
                  e.stopPropagation();
                  actionOnClick();
                }}
              >
                {label}
              </ToastAction>
            );
          }
        }

        return (
          <Toast key={id} data-testid="glass-toast" variant={variant} onClick={onClick} {...props}>
            <div className="flex items-start gap-3">
              {icon ?? <VariantIcon variant={variant} />}
              <div className="grid min-w-0 gap-1">
                {title && <ToastTitle>{title}</ToastTitle>}
                {description && <ToastDescription>{description}</ToastDescription>}
              </div>
            </div>
            {actionNode}
            {/*
              stopPropagation: the toast root carries an optional `onClick` that
              navigates to the record an undo toast is about (useUndoAction).
              Radix's ToastClose renders inside that root, so without this the ✕
              dismisses the toast AND yanks the user off the current list.
              Composed ahead of Radix's own onClose, which still runs.
            */}
            <ToastClose onClick={(e) => e.stopPropagation()} />
          </Toast>
        );
      })}
      <ToastViewport aria-live="polite" />
    </ToastProvider>
  );

  // Render via portal to escape screen-filter-wrapper
  if (!mounted) {
    return null;
  }

  return createPortal(toasterContent, document.body);
}
